"use client"

/**
 * Shared motion vocabulary for Hiveku gallery components.
 *
 * Use these primitives instead of building per-component motion islands.
 * Each wrapper is opt-in — wrap an existing element with one of these and
 * the motion treatment activates. Server-rendered content inside stays
 * server-rendered (the wrapper is the only client boundary).
 *
 * Vocabulary:
 *   <MagneticCta>          — Button pulls toward cursor with spring physics
 *   <TiltCard>             — 3D mouse-tilt on a card (perspective + rotateX/Y)
 *   <LiftCard>             — y:-4 scale:1.02 on hover, spring snap-back
 *   <ParallaxScale>        — scroll-linked scale + drift for media tiles
 *   <AmbientGlow>          — looping pulse on a decorative blob/glow
 *   <RevealStagger>        — viewport-entry container that staggers children
 *   <RevealItem>           — child of RevealStagger (use inside)
 *   <CountUpNumber>        — animated count from 0 → target on viewport entry
 *   <ScrollCueChevron>     — bouncing scroll-down indicator with fade
 *   <PulseRing>            — radiating ring around child (great for badges)
 *
 * Quality bar per [[feedback_gallery_quality_bar]]: every component in the
 * gallery should feel alive — scroll-linked + viewport-entry + hover-responsive
 * + magnetic CTAs. These primitives are the lego bricks.
 */

import { motion, useScroll, useTransform, useMotionValue, useSpring, useInView, animate, type Variants } from "framer-motion"
import { ArrowDown } from "lucide-react"
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react"

import { cn } from "@/lib/utils"

// ──────────────────────────────────────────────────────────────────────────────
// MagneticCta — pull-toward-cursor wrapper for CTAs
// ──────────────────────────────────────────────────────────────────────────────

export function MagneticCta({
  children,
  className,
  strength = 14,
}: {
  children: ReactNode
  className?: string
  /** Max translation in px (default 14). Tune up for more dramatic pull. */
  strength?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const sx = useSpring(x, { stiffness: 220, damping: 20, mass: 0.3 })
  const sy = useSpring(y, { stiffness: 220, damping: 20, mass: 0.3 })

  function handleMove(e: MouseEvent<HTMLDivElement>) {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    x.set(((e.clientX - rect.left) / rect.width - 0.5) * strength)
    y.set(((e.clientY - rect.top) / rect.height - 0.5) * (strength * 0.75))
  }
  function handleLeave() {
    x.set(0)
    y.set(0)
  }

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{ x: sx, y: sy }}
      className={cn("inline-flex", className)}
    >
      {children}
    </motion.div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// TiltCard — 3D mouse-tilt with perspective
// ──────────────────────────────────────────────────────────────────────────────

export function TiltCard({
  children,
  className,
  intensity = 6,
}: {
  children: ReactNode
  className?: string
  /** Max rotation in degrees (default 6). Subtle: 3-4. Dramatic: 8-12. */
  intensity?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const rx = useMotionValue(0)
  const ry = useMotionValue(0)
  const srx = useSpring(rx, { stiffness: 180, damping: 22, mass: 0.4 })
  const sry = useSpring(ry, { stiffness: 180, damping: 22, mass: 0.4 })

  function handleMove(e: MouseEvent<HTMLDivElement>) {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width - 0.5
    const y = (e.clientY - rect.top) / rect.height - 0.5
    ry.set(x * intensity * 2)
    rx.set(-y * intensity * 2)
  }
  function handleLeave() {
    rx.set(0)
    ry.set(0)
  }

  return (
    <div style={{ perspective: 1400 }} className={cn("relative", className)}>
      <motion.div
        ref={ref}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
        className="h-full w-full"
      >
        {children}
      </motion.div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// LiftCard — hover lift + scale wrapper for cards/tiles
// ──────────────────────────────────────────────────────────────────────────────

export function LiftCard({
  children,
  className,
  lift = 4,
  scale = 1.02,
}: {
  children: ReactNode
  className?: string
  lift?: number
  scale?: number
}) {
  return (
    <motion.div
      whileHover={{ y: -lift, scale, transition: { type: "spring", stiffness: 280, damping: 22 } }}
      className={cn("h-full", className)}
    >
      {children}
    </motion.div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// ParallaxScale — scroll-linked scale + drift for media tiles
// ──────────────────────────────────────────────────────────────────────────────

export function ParallaxScale({
  children,
  className,
  scaleRange = [1.06, 1.0, 1.06],
  driftPx = 16,
}: {
  children: ReactNode
  className?: string
  scaleRange?: [number, number, number]
  driftPx?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] })
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], scaleRange)
  const y = useTransform(scrollYProgress, [0, 1], [driftPx, -driftPx])

  return (
    <div ref={ref} className={cn("relative", className)}>
      <motion.div style={{ scale, y }} className="h-full w-full">
        {children}
      </motion.div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// AmbientGlow — looping pulse blob
// ──────────────────────────────────────────────────────────────────────────────

export function AmbientGlow({ className, duration = 6.5 }: { className?: string; duration?: number }) {
  return (
    <motion.div
      aria-hidden="true"
      className={cn("absolute -z-10 rounded-full bg-primary/12 blur-3xl", className)}
      animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
      transition={{ duration, ease: "easeInOut", repeat: Infinity }}
    />
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// RevealStagger + RevealItem — viewport-entry staggered fade-in
// ──────────────────────────────────────────────────────────────────────────────

const REVEAL_PARENT: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
}

const REVEAL_CHILD: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
}

export function RevealStagger({
  children,
  className,
  staggerChildren = 0.12,
  delayChildren = 0.1,
}: {
  children: ReactNode
  className?: string
  staggerChildren?: number
  delayChildren?: number
}) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-15%" }}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren, delayChildren } },
      }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

export function RevealItem({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div variants={REVEAL_CHILD} className={className}>
      {children}
    </motion.div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// CountUpNumber — animated count from 0 → target on viewport entry
// ──────────────────────────────────────────────────────────────────────────────

export function CountUpNumber({
  target,
  prefix = "",
  suffix = "",
  duration = 1.6,
  className,
}: {
  target: number
  prefix?: string
  suffix?: string
  duration?: number
  className?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const [display, setDisplay] = useState(0)
  const inView = useInView(ref, { once: true, margin: "-15%" })

  useEffect(() => {
    if (!inView) return
    const controls = animate(0, target, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    })
    return () => controls.stop()
  }, [inView, target, duration])

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// ScrollCueChevron — bouncing scroll-down indicator that fades on scroll
// ──────────────────────────────────────────────────────────────────────────────

export function ScrollCueChevron({ label = "Scroll", className }: { label?: string; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] })
  const opacity = useTransform(scrollYProgress, [0, 0.25], [1, 0])

  return (
    <motion.div
      ref={ref}
      style={{ opacity }}
      className={cn("absolute bottom-8 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-2", className)}
    >
      <span className="text-[10px] font-medium uppercase tracking-[0.32em]">{label}</span>
      <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, ease: "easeInOut", repeat: Infinity }}>
        <ArrowDown className="size-5" />
      </motion.div>
    </motion.div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// PulseRing — radiating ring around child (rating badge, emergency CTA halo)
// ──────────────────────────────────────────────────────────────────────────────

export function PulseRing({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("relative inline-block", className)}>
      <motion.span
        aria-hidden="true"
        className="absolute inset-0 rounded-full border-2 border-primary/40"
        animate={{ scale: [1, 1.18, 1], opacity: [0.7, 0, 0.7] }}
        transition={{ duration: 2.4, ease: "easeInOut", repeat: Infinity }}
      />
      <div className="relative">{children}</div>
    </div>
  )
}
